import { PersonaInitialsColor } from '@fluentui/react';
import React, { useEffect, useState } from 'react';
import { Icon } from '@fluentui/react';
import { Link, useLocation, useHistory } from 'react-router-dom';
import useStore, { IStore } from 'app/store';

import './Header.scss';
import { ContextualMenu, IContextualMenuItem } from '@fluentui/react';
import { useMsal } from '@azure/msal-react';

import SearchBarWithSuggestions from 'app/modules/admin/searchBarWithSuggestions/SearchBarWithSuggestions';
import { getRoles, isUser } from 'app/utils/authUtilities';
import { keyPress, quickSearchVisibility } from 'app/components/helpers';
import NotificationOverLay from 'app/modules/admin/notifications/notificationsOverflow/NotificationOverLay';
import { useGetArticleCategories } from 'app/services/article-api';
import { useNotifications, useNotificationsCount, useNotificationsRead } from 'app/services/user-api';
import { startCase } from 'lodash';
import { RouteConstants, StringConstants } from 'app/utils/constants';
import Avatar from '../avatar/Avatar';
import { Trans } from 'react-i18next';
import { UserDto } from 'generated/request-backend';

const { END_USER } = StringConstants;

const Header: React.FC = () => {
    const { instance } = useMsal();
    const history = useHistory();
    const { pathname } = useLocation();
    const splitLocation = pathname.split('/').filter((path) => path);
    const isCA = pathname === '/manage-content/article/create';
    const isHR = pathname === RouteConstants.HELP_REQUEST;

    const [isNotificationVisible, setIsNotificationVisible] = useState(false);
    const [tempNotificationsCount, setTempNotificationsCount] = useState('0');
    const [bellAnimation, setBellAnimation] = useState(false);
    const [showContextualMenuUser, setShowContextualMenuUser] = React.useState(false);

    const userRef = React.useRef<HTMLDivElement>(null);
    const notificationsContainer = React.useRef<HTMLDivElement>(null);

    const userDetails: UserDto = useStore((state: IStore) => state.userDetails as UserDto);
    const setUserSearchValue = useStore((state: IStore) => state.setUserSearchValue);

    const { data: notificationsCount, refetch: getNotifications, isFetched: IsNotificationsFetched } = useNotificationsCount();
    const { mutate: notificationsRequest, data: notifications } = useNotifications();
    const { mutateAsync: markAllAsRead } = useNotificationsRead();
    const { isFetched: isFetchedCategories, data: articleCategories } = useGetArticleCategories();
    const setArticleCategories = useStore((state) => state.setArticleCategories);

    const handleLogout = () => {
        localStorage.clear();
        instance.logoutRedirect({
            postLogoutRedirectUri: '/'
        });
    };
    const handleUserProfile = () => {
        history.push(RouteConstants.PROFILE);
    };

    const userItems: IContextualMenuItem[] = [
        {
            key: 'profile',
            text: 'Profile',
            onClick: handleUserProfile
        },
        {
            key: 'sign out',
            text: 'Sign Out',
            onClick: handleLogout
        }
    ];

    const onShowContextualMenu = React.useCallback((ev: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLDivElement>) => {
        ev.preventDefault(); // don't navigate
        setIsNotificationVisible((prevVal) => !prevVal);
    }, []);

    const onShowContextualMenuUser = React.useCallback(
        (ev: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLDivElement>) => {
            ev.preventDefault(); // don't navigate
            setShowContextualMenuUser(!showContextualMenuUser);
        },
        []
    );
    const onHideContextualMenuUser = React.useCallback(() => setShowContextualMenuUser(false), []);

    const handleNotificationClose = () => {
        setIsNotificationVisible(false);

        markAllAsRead().then(() => {
            setInterval(() => {
                setBellAnimation(false);
            }, 15000);
            setTempNotificationsCount('0');
        });
    };

    const useNotificationClose = (ref: React.RefObject<HTMLDivElement>, handleSuggestionClose: Function) => {
        React.useEffect(() => {
            function handleClickOutside(event: MouseEvent) {
                if (ref.current && !ref.current.contains(event.target as Node)) {
                    handleSuggestionClose();
                }
            }

            document.addEventListener('mousedown', handleClickOutside);
            return () => {
                // Unbind the event listener on clean up
                document.removeEventListener('mousedown', handleClickOutside);
            };
        }, [handleSuggestionClose, ref]);
    };

    useEffect(() => {
        setTempNotificationsCount(notificationsCount > 99 ? '99+' : notificationsCount?.toString());
        if (notificationsCount > 0) {
            notificationsRequest({
                pageNumber: 1,
                pageSize: notificationsCount > 5 ? 5 : notificationsCount
            });
            setBellAnimation(true);
            setInterval(() => {
                setBellAnimation(false);
            }, 15000);
        }
    }, [notificationsCount, notificationsRequest]);

    useEffect(() => {
        const fetchNotifications = setInterval(() => {
            if (!IsNotificationsFetched) getNotifications();
        }, 30000);
        return () => {
            clearInterval(fetchNotifications);
        };
    }, []);

    useEffect(() => {
        setArticleCategories(articleCategories);
    }, [isFetchedCategories]);

    useNotificationClose(notificationsContainer, handleNotificationClose);

    const isActiveGlobalNav = (navItem: 'article' | 'news') => {
        switch (navItem) {
            case 'article':
                return ['/knowledge-articles/home', RouteConstants.SEARCH_RESULTS_ARTICLES, RouteConstants.ALL_ARTICLES].includes(
                    pathname
                );
            case StringConstants.NEWS:
                return ['/news-feed/home', RouteConstants.SEARCH_RESULTS_NEWS, RouteConstants.ALL_NEWS].includes(pathname);
            default:
                break;
        }
    };
    return (
        <div className="ms-Grid p-a-0 drop-shadow" dir="ltr">
            <div className="header">
                <div className="container">
                    <div className="ms-Grid-row">
                        <div className="ms-Grid-col  ms-sm12 ms-lg6">
                            <div className="header-text" role="navigation">
                                <Link aria-label="admin dashboard link" role="link" to="/" onClick={() => setUserSearchValue('')}>
                                    <Trans>Surface Knowledge Portal</Trans>
                                </Link>
                            </div>
                        </div>

                        <div className="ms-Grid-col ms-sm12 ms-lg6">
                            <div className="user-menu">
                                <div>
                                    <Link role="link" to="/submit-feedback" data-testid="feedback-link" className="notif-button feedback-link" aria-label="submit feedback">
                                        <Icon
                                            id="feedbackHeader"
                                            className="feedback-icon"
                                            iconName="Feedback"
                                            aria-label="feedback"
                                        />
                                    </Link>
                                </div>
                                <div
                                    onClick={onShowContextualMenu}
                                    tabIndex={0}
                                    onKeyPress={(e) => keyPress(e, () => onShowContextualMenu(e))}
                                    className="notif-button"
                                    aria-label="notification"
                                    role="button">
                                        <Icon
                                            id="notificationHeader"
                                            aria-hidden="true"
                                            className={`bell-icon ${bellAnimation && !isCA && !isHR ? 'animate' : ''}`}
                                            iconName="Ringer"
                                            aria-label="bell"
                                        />
                                    {tempNotificationsCount > '0' && (
                                        <div className={`badge ${tempNotificationsCount === '99+' ? 'badge--xl' : ''}`}>
                                            {tempNotificationsCount}
                                        </div>
                                    )}
                                </div>
                                {isNotificationVisible && (
                                    <div ref={notificationsContainer} className="notification-container">
                                        <NotificationOverLay
                                            handleNotificationClose={handleNotificationClose}
                                            items={tempNotificationsCount > '0' ? notifications?.items : []}
                                        />
                                    </div>
                                )}

                                {userDetails?.id && (
                                    <div id="profileHeader" className="profile border-left">
                                        <div className="display-pic">
                                            {userDetails && (
                                                <Avatar
                                                    userId={userDetails.id}
                                                    userName={userDetails.displayName}
                                                    initialColor={PersonaInitialsColor.teal}
                                                />
                                            )}
                                        </div>
                                        <div
                                            className="userName"
                                            ref={userRef}
                                            tabIndex={0}
                                            onKeyPress={(e) => keyPress(e, () => onShowContextualMenuUser(e))}
                                            onClick={onShowContextualMenuUser}>
                                            {userDetails?.displayName}
                                            <br />
                                            <small>
                                                <Trans>
                                                    {getRoles(userDetails?.userRoles)?.length > 0
                                                        ? getRoles(userDetails?.userRoles)
                                                              .map((item) => startCase(item))
                                                              .join(', ')
                                                        : startCase(END_USER).split(' ')[1]}
                                                </Trans>
                                            </small>
                                        </div>

                                        <ContextualMenu
                                            items={userItems}
                                            hidden={!showContextualMenuUser}
                                            target={userRef}
                                            onItemClick={onHideContextualMenuUser}
                                            onDismiss={onHideContextualMenuUser}
                                            ariaLabel="profile-menu"
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="container">
                <div className="bottom-header">
                    <nav className="navigation_link">
                        <ul>
                            <li className={`c-pointer ${splitLocation.reverse()[0] ? '' : 'active'}`}>
                                <Link role="link" to="/" className='home-link'>
                                        <div
                                            aria-label="home"
                                            role="navigation"
                                            onClick={() => setUserSearchValue("")}
                                            className={`${isUser(userDetails?.userRoles) ? "home-icon" : "admin-home-icon"}`}>
                                                {isUser(userDetails?.userRoles) ? 
                                                    <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                        <path d="M10.5495 2.53189C11.3874 1.82531 12.6126 1.82531 13.4505 2.5319L20.2005 8.224C20.7074 8.65152 21 9.2809 21 9.94406L21 19.2539C21 20.2204 20.2165 21.0039 19.25 21.0039H15.75C14.7835 21.0039 14 20.2204 14 19.2539L14 14.2468C14 14.1088 13.8881 13.9968 13.75 13.9968H10.25C10.1119 13.9968 9.99999 14.1088 9.99999 14.2468L9.99999 19.2539C9.99999 20.2204 9.2165 21.0039 8.25 21.0039H4.75C3.7835 21.0039 3 20.2204 3 19.2539V9.94406C3 9.2809 3.29255 8.65152 3.79952 8.224L10.5495 2.53189ZM12.4835 3.6786C12.2042 3.44307 11.7958 3.44307 11.5165 3.6786L4.76651 9.37071C4.59752 9.51321 4.5 9.72301 4.5 9.94406L4.5 19.2539C4.5 19.392 4.61193 19.5039 4.75 19.5039H8.25C8.38807 19.5039 8.49999 19.392 8.49999 19.2539L8.49999 14.2468C8.49999 13.2803 9.2835 12.4968 10.25 12.4968H13.75C14.7165 12.4968 15.5 13.2803 15.5 14.2468L15.5 19.2539C15.5 19.392 15.6119 19.5039 15.75 19.5039H19.25C19.3881 19.5039 19.5 19.392 19.5 19.2539L19.5 9.94406C19.5 9.72301 19.4025 9.51321 19.2335 9.37071L12.4835 3.6786Z" />
                                                    </svg> : 
                                                    <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                        <path d="M16 5C16 3.61929 17.1193 2.5 18.5 2.5C19.8807 2.5 21 3.61929 21 5V5.25C21 5.66421 21.3358 6 21.75 6C22.1642 6 22.5 5.66421 22.5 5.25V5C22.5 2.79086 20.7091 1 18.5 1C16.2909 1 14.5 2.79086 14.5 5V7H7.25C5.45507 7 4 8.45507 4 10.25V17.75C4 19.5449 5.45507 21 7.25 21H16.75C18.5449 21 20 19.5449 20 17.75V10.25C20 8.45507 18.5449 7 16.75 7H16V5ZM7.25 8.5H16.75C17.7165 8.5 18.5 9.2835 18.5 10.25V17.75C18.5 18.7165 17.7165 19.5 16.75 19.5H7.25C6.2835 19.5 5.5 18.7165 5.5 17.75V10.25C5.5 9.2835 6.2835 8.5 7.25 8.5ZM12 15.5C12.8284 15.5 13.5 14.8284 13.5 14C13.5 13.1716 12.8284 12.5 12 12.5C11.1716 12.5 10.5 13.1716 10.5 14C10.5 14.8284 11.1716 15.5 12 15.5Z" />
                                                    </svg>
                                                }
                                        </div>
                                </Link>
                            </li>

                            {!isUser(userDetails?.userRoles) && (
                                <li
                                    className={
                                        splitLocation.reverse()[0] === 'knowledge-portal' ||
                                        splitLocation.reverse()[1] === 'unified-dashboard' ||
                                        splitLocation.reverse()[0] === 'unified-dashboard'
                                            ? 'active'
                                            : ''
                                    }>
                                    <Link to="/knowledge-portal" onClick={() => setUserSearchValue('')}>
                                        <Trans>Knowledge Portal</Trans>
                                    </Link>
                                </li>
                            )}
                            <li className={isActiveGlobalNav('article') ? 'active' : ''}>
                                <Link role="link" to="/knowledge-articles/home">
                                    <Trans>Knowledge Articles</Trans>
                                </Link>
                            </li>

                            <li className={isActiveGlobalNav('news') ? 'active' : ''}>
                                <Link role="link" to="/news-feed/home">
                                    <Trans>News Feed</Trans>
                                </Link>
                            </li>
                        </ul>
                    </nav>

                    {quickSearchVisibility(splitLocation.join('/')) && <SearchBarWithSuggestions />}
                </div>
            </div>
        </div>
    );
};

export default Header;
